import React, { useState, useEffect } from "react";
import { Box, Flex, useToast, Button, Text, Spacer, Heading } from "@chakra-ui/react";
import { AiOutlineArrowLeft } from "react-icons/ai";

import preferencesService from "../../services/preferences.service";
import rulesService from "../../services/rules.service";
import countriesService from "../../services/countries.service";
import CountrySideList from "./CountrySideList";
import ColumnTransfer from "./ColumnTransfer";

const DEFAULT_KEY = "default";

// Resolve the caseTriggerView for a country from the latest stored preferences,
// falling back to the default view when there's no per-country override.
const viewForSelected = (code) => {
  const prefs = JSON.parse(localStorage.getItem("preferences")) || {};
  if (code === DEFAULT_KEY) return [...(prefs.caseTriggerView || [])];
  const byc = prefs.caseTriggerViewByCountry || {};
  return [...(byc[code] && byc[code].length ? byc[code] : prefs.caseTriggerView || [])];
};

function CaseTriggerViewEdit(props) {
  const toast = useToast();
  const preferences = JSON.parse(localStorage.getItem("preferences"));
  // Base the case-view picker on the full merchantViewMaster, plus any rule columns
  // (e.g. newly-added headings) not already present.
  const master = preferences.merchantViewMaster || [];
  const [pickerItems, setPickerItems] = useState([...master]);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_KEY);
  const [dbCountries, setDbCountries] = useState([]);
  const [caseTriggerView, setCaseTriggerView] = useState(viewForSelected(DEFAULT_KEY));

  useEffect(() => {
    rulesService.getColumns().then((res) => {
      if (res && res.columns) {
        const existing = new Set(master.map((i) => i.value));
        const extra = res.columns.filter((c) => !existing.has(c)).map((c) => ({ value: c, label: c }));
        if (extra.length) setPickerItems([...master, ...extra]);
      }
    });
    countriesService.getCountries().then((res) => {
      if (res && res.countries) setDbCountries(res.countries);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCountryChange = (code) => {
    setSelectedCountry(code);
    setCaseTriggerView(viewForSelected(code));
  };

  const countryItems = [{ code: DEFAULT_KEY, name: "Default" }, ...dbCountries];
  const isCustom = (code) => {
    const m = (JSON.parse(localStorage.getItem("preferences")) || {}).caseTriggerViewByCountry || {};
    return !!m[code];
  };

  const persist = (mutate) => {
    const prefs = JSON.parse(localStorage.getItem("preferences")) || {};
    mutate(prefs);
    localStorage.setItem("preferences", JSON.stringify(prefs));
  };

  const handleSave_caseTriggerView = async () => {
    props.setisLoading(true);
    const payload =
      selectedCountry === DEFAULT_KEY
        ? { caseTriggerView }
        : { ["caseTriggerViewByCountry." + selectedCountry]: caseTriggerView };
    const res = await preferencesService.update(payload);
    props.setisLoading(false);
    if (res && (res.warning || res.error)) {
      toast({ title: res.warning || res.error, position: "top", status: "error", isClosable: true });
      setCaseTriggerView(viewForSelected(selectedCountry));
      return;
    }
    persist((prefs) => {
      if (selectedCountry === DEFAULT_KEY) {
        prefs.caseTriggerView = caseTriggerView;
      } else {
        prefs.caseTriggerViewByCountry = prefs.caseTriggerViewByCountry || {};
        prefs.caseTriggerViewByCountry[selectedCountry] = caseTriggerView;
      }
    });
    toast({ title: `Updated...!`, position: "top", status: "success", isClosable: true });
  };

  const handleResetCountry = async () => {
    props.setisLoading(true);
    const res = await preferencesService.update({ unset: ["caseTriggerViewByCountry." + selectedCountry] });
    props.setisLoading(false);
    if (res && (res.warning || res.error)) {
      toast({ title: res.warning || res.error, position: "top", status: "error", isClosable: true });
      return;
    }
    persist((prefs) => {
      if (prefs.caseTriggerViewByCountry) delete prefs.caseTriggerViewByCountry[selectedCountry];
    });
    setCaseTriggerView(viewForSelected(selectedCountry));
    toast({ title: `Reset ${selectedCountry} to default`, position: "top", status: "success", isClosable: true });
  };

  return (
    <Box w="100%">
      <Flex alignItems="center" mb={3}>
        <Button size="xs" leftIcon={<AiOutlineArrowLeft />} variant="ghost" onClick={props.onBack}>
          Back to settings
        </Button>
        <Spacer />
        <Heading size="sm">Case view column preference</Heading>
        <Spacer />
      </Flex>
      <Flex align="flex-start">
        <CountrySideList items={countryItems} selected={selectedCountry} onSelect={onCountryChange} isCustom={isCustom} />
        <Box flex="1">
          {selectedCountry !== DEFAULT_KEY && (
            <Flex mb={2} alignItems="center">
              <Text fontSize="sm" color="gray.600">Editing the <b>{selectedCountry}</b> override</Text>
              <Spacer />
              <Button size="xs" variant="outline" colorScheme="red" onClick={handleResetCountry}>
                Reset to default
              </Button>
            </Flex>
          )}
          <ColumnTransfer items={pickerItems} selected={caseTriggerView} onChange={setCaseTriggerView} />
          <Box>
            <Button size="sm" fontSize=".8rem" mr={3} onClick={handleSave_caseTriggerView} colorScheme="blue">
              Save
            </Button>
            <Button size="sm" fontSize=".8rem" mr={3} onClick={() => setCaseTriggerView([...pickerItems])}>
              Select all
            </Button>
            <Button size="sm" fontSize=".8rem" mr={3} onClick={() => setCaseTriggerView([])}>
              Unselect all
            </Button>
            <Button size="sm" fontSize=".8rem" mr={3} onClick={() => setCaseTriggerView(viewForSelected(selectedCountry))}>
              Discard changes
            </Button>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

export default CaseTriggerViewEdit;
